import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";

interface LanguageSwitcherProps {
  className?: string;
  showLabel?: boolean;
}

const languages = [
  { code: "fr", flag: "🇫🇷", label: "Français" },
  { code: "en", flag: "🇬🇧", label: "English" },
] as const;

type LanguageCode = (typeof languages)[number]["code"];

export function LanguageSwitcher({
  className,
  showLabel = false,
}: LanguageSwitcherProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const currentLang =
    languages.find((l) => l.code === i18n.language) ?? languages[0];

  const nextLang = languages.find((l) => l.code !== currentLang.code)!;

  const switchLanguage = useCallback(() => {
    const newLang: LanguageCode = nextLang.code;

    // Update i18next language
    i18n.changeLanguage(newLang);

    // Persist choice in localStorage
    try {
      localStorage.setItem("katalog-language", newLang);
    } catch {
      // Ignore storage errors
    }

    // Update URL path: replace the language prefix in the current path
    // e.g., /fr/catalog → /en/catalog
    const pathSegments = location.pathname.split("/").filter(Boolean);

    if (
      pathSegments.length > 0 &&
      languages.some((l) => l.code === pathSegments[0])
    ) {
      // Replace the existing language segment
      pathSegments[0] = newLang;
    } else {
      // Prepend the language segment
      pathSegments.unshift(newLang);
    }

    const newPath =
      "/" + pathSegments.join("/") + location.search + location.hash;
    navigate(newPath, { replace: true });

    // If user is logged in, also update preferred_language via API
    // (fire-and-forget; AuthContext will pick up the change)
    const accessToken = localStorage.getItem("katalog-access-token");
    if (accessToken) {
      import("@/api/auth").then(({ updateProfile }) => {
        updateProfile({ preferred_language: newLang }).catch(() => {
          // Silently ignore — the local change is already applied
        });
      });
    }
  }, [i18n, navigate, location, nextLang]);

  return (
    <button
      type="button"
      onClick={switchLanguage}
      className={cn(
        "relative inline-flex items-center gap-1.5 rounded-md px-2 py-1.5",
        "text-sm font-medium transition-colors duration-200",
        "hover:bg-accent hover:text-accent-foreground",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        className,
      )}
      aria-label={t("accessibility.switch_to_language", {
        language: nextLang.label,
      })}
      title={t("accessibility.switch_to_language", {
        language: nextLang.label,
      })}
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={currentLang.code}
          initial={{ rotateY: 90, opacity: 0 }}
          animate={{ rotateY: 0, opacity: 1 }}
          exit={{ rotateY: -90, opacity: 0 }}
          transition={{
            type: "spring",
            stiffness: 300,
            damping: 25,
            duration: 0.3,
          }}
          className="inline-block text-lg leading-none"
          style={{ transformStyle: "preserve-3d" }}
        >
          {currentLang.flag}
        </motion.span>
      </AnimatePresence>

      {showLabel && (
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={currentLang.code + "-label"}
            initial={{ opacity: 0, x: -4 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 4 }}
            transition={{ duration: 0.2 }}
            className="text-xs font-semibold uppercase tracking-wider"
          >
            {currentLang.code.toUpperCase()}
          </motion.span>
        </AnimatePresence>
      )}

      {/* Small indicator showing the language you'll switch TO */}
      <span className="sr-only">
        {t("accessibility.current_language_desc", {
          current: currentLang.label,
          next: nextLang.label,
        })}
      </span>
    </button>
  );
}

export default LanguageSwitcher;
